import React, { useMemo, useState } from 'react';
import {
    Button,
    Card,
    Col,
    Divider,
    Empty,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
    Steps,
    Table,
    Tabs,
    Tag,
    Typography,
    message,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
    SAVE_WORKFLOW_TEMPLATE,
    SaveWorkflowTemplateResponse,
    SaveWorkflowTemplateVariables,
} from './graphql/mutations/saveWorkflowTemplate';
import {
    UPDATE_WORKFLOW_TEMPLATE,
    UpdateWorkflowTemplateResponse,
    UpdateWorkflowTemplateVariables,
} from './graphql/mutations/updateWorkflowTemplate';
import {
    GET_WORKFLOW_LATEST_TEMPLATE_BY_BIZ_TYPE,
    GetWorkflowLatestTemplateByBizTypeResponse,
    GetWorkflowLatestTemplateByBizTypeVariables,
    WorkflowTemplateType,
} from './graphql/queries/getWorkflowLatestTemplateByBizType';
import { resolveWorkflowErrorMessage } from './utils/workflowErrorMessage';
import {HasResource} from "../../../components/HasResource";
import {ResourceCode} from "../../../constants/resourceCodes";
import {
    GET_ACCOUNT_LISTING_FOR_HEADQUARTERS,
    GetAccountListingForHeadquartersResponse,
} from '../account/graphql/queries/getAccountListingForHeadquarters';

const { Text, Title } = Typography;

type NodeType = 'APPROVAL' | 'CC' | 'SYSTEM';

interface WorkflowNodeConfig {
    id: string;
    nodeKey: string;
    nodeName: string;
    nodeType: NodeType;
    approverUserIds: string;
}

interface WorkflowTemplateForm {
    templateKey: string;
    name: string;
    bizType: string;
    version: number;
    status: number;
    description?: string;
    nodes: WorkflowNodeConfig[];
}

interface WorkflowDefinitionNodeView {
    nodeKey: string;
    nodeName: string;
    nodeType: number;
    order: number;
    userIds?: number[];
}

const nodeTypeColorMap: Record<NodeType, string> = {
    APPROVAL: 'blue',
    CC: 'purple',
    SYSTEM: 'gold',
};

const nodeTypeLabelMap: Record<number, string> = {
    1: 'APPROVAL',
    2: 'CC',
    3: 'SYSTEM',
};

const nodeTypeTagColorMap: Record<number, string> = {
    1: 'blue',
    2: 'purple',
    3: 'gold',
};

const formatApproverDisplay = (
    userIds: number[] | undefined,
    accountNameById: Map<number, string>,
): string => {
    if (!userIds?.length) {
        return '-';
    }
    return userIds
        .map((id) => {
            const name = accountNameById.get(id);
            return name ? `${name} (${id})` : String(id);
        })
        .join(', ');
};

const nodeTypeFromCode = (code: number): NodeType => {
    if (code === 2) {
        return 'CC';
    }
    if (code === 3) {
        return 'SYSTEM';
    }
    return 'APPROVAL';
};

const buildDefinitionJson = (nodes: WorkflowNodeConfig[] | undefined) =>
    JSON.stringify({
        nodes: (nodes || []).map((node, index) => ({
            nodeKey: node.nodeKey,
            nodeName: node.nodeName,
            nodeType:
                node.nodeType === 'APPROVAL'
                    ? 1
                    : node.nodeType === 'CC'
                      ? 2
                      : 3,
            order: index + 1,
            userIds: (node.approverUserIds || '')
                .split(',')
                .map((id) => Number(id.trim()))
                .filter((id) => !Number.isNaN(id)),
        })),
    });

const templateToFormValues = (template: WorkflowTemplateType): WorkflowTemplateForm => {
    let nodes: WorkflowNodeConfig[] = [];
    try {
        const parsed = JSON.parse(template.definitionJson) as { nodes?: WorkflowDefinitionNodeView[] };
        nodes = (parsed.nodes || [])
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((node, index) => ({
                id: `${node.nodeKey}_${index}`,
                nodeKey: node.nodeKey,
                nodeName: node.nodeName,
                nodeType: nodeTypeFromCode(node.nodeType),
                approverUserIds: (node.userIds || []).join(', '),
            }));
    } catch {
        nodes = [];
    }
    return {
        templateKey: template.templateKey,
        name: template.name,
        bizType: template.bizType,
        version: template.version,
        status: template.status,
        description: template.description ?? undefined,
        nodes,
    };
};

const buildWorkflowTemplatePayload = (values: WorkflowTemplateForm) => ({
    templateKey: values.templateKey,
    name: values.name,
    bizType: values.bizType,
    version: values.version,
    status: values.status,
    description: values.description,
    definitionJson: buildDefinitionJson(values.nodes),
});

const WorkflowTemplateDesignerPage: React.FC = () => {
    const [form] = Form.useForm<WorkflowTemplateForm>();
    const [editForm] = Form.useForm<WorkflowTemplateForm>();
    const [queryForm] = Form.useForm<{ bizType: string }>();
    const [saveTemplate, { loading: saving }] = useMutation<
        SaveWorkflowTemplateResponse,
        SaveWorkflowTemplateVariables
    >(SAVE_WORKFLOW_TEMPLATE);
    const [updateTemplate, { loading: updating }] = useMutation<
        UpdateWorkflowTemplateResponse,
        UpdateWorkflowTemplateVariables
    >(UPDATE_WORKFLOW_TEMPLATE);
    const [queryLatestTemplate, { loading: querying, data: queryData }] = useLazyQuery<
        GetWorkflowLatestTemplateByBizTypeResponse,
        GetWorkflowLatestTemplateByBizTypeVariables
    >(GET_WORKFLOW_LATEST_TEMPLATE_BY_BIZ_TYPE, { fetchPolicy: 'network-only' });
    const [previewNodes, setPreviewNodes] = useState<WorkflowNodeConfig[]>([]);
    const [editPreviewNodes, setEditPreviewNodes] = useState<WorkflowNodeConfig[]>([]);
    const [isEditing, setIsEditing] = useState(false);

    const previewSteps = useMemo(
        () =>
            previewNodes.map((node, index) => ({
                key: node.id || `${node.nodeKey}_${index}`,
                title: node.nodeName || `节点${index + 1}`,
                description: (
                    <Space direction="vertical" size={2}>
                        <Tag color={nodeTypeColorMap[node.nodeType || 'APPROVAL']}>
                            {node.nodeType || 'APPROVAL'}
                        </Tag>
                        <Text type="secondary">key: {node.nodeKey || '-'}</Text>
                        <Text type="secondary">
                            审批人: {node.approverUserIds || '-'}
                        </Text>
                    </Space>
                ),
            })),
        [previewNodes],
    );

    const editPreviewSteps = useMemo(
        () =>
            editPreviewNodes.map((node, index) => ({
                key: node.id || `${node.nodeKey}_${index}`,
                title: node.nodeName || `节点${index + 1}`,
                description: (
                    <Space direction="vertical" size={2}>
                        <Tag color={nodeTypeColorMap[node.nodeType || 'APPROVAL']}>
                            {node.nodeType || 'APPROVAL'}
                        </Tag>
                        <Text type="secondary">key: {node.nodeKey || '-'}</Text>
                        <Text type="secondary">
                            审批人: {node.approverUserIds || '-'}
                        </Text>
                    </Space>
                ),
            })),
        [editPreviewNodes],
    );

    const nodeColumns = [
        {
            title: '节点Key',
            dataIndex: 'nodeKey',
            key: 'nodeKey',
        },
        {
            title: '节点名称',
            dataIndex: 'nodeName',
            key: 'nodeName',
        },
        {
            title: '类型',
            dataIndex: 'nodeType',
            key: 'nodeType',
            render: (nodeType: NodeType) => (
                <Tag color={nodeTypeColorMap[nodeType]}>{nodeType}</Tag>
            ),
        },
        {
            title: '审批人ID列表',
            dataIndex: 'approverUserIds',
            key: 'approverUserIds',
        },
    ];

    const handlePreview = async () => {
        try {
            const values = await form.validateFields();
            setPreviewNodes(values.nodes || []);
        } catch {
            message.warning('请先完善模板必填信息再预览');
        }
    };

    const handleBuildPayload = async () => {
        try {
            const values = await form.validateFields();
            const payload = buildWorkflowTemplatePayload(values);
            await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
            message.success('已生成模板JSON并复制到剪贴板');
        } catch {
            message.error('生成失败，请检查表单配置');
        }
    };

    const handleSaveTemplate = async () => {
        try {
            const values = await form.validateFields();
            const payload = buildWorkflowTemplatePayload(values);

            const result = await saveTemplate({
                variables: {
                    input: payload,
                },
            });
            if (result.data?.saveWorkflowTemplate) {
                message.success('流程模板保存成功');
                return;
            }
            message.error('流程模板保存失败，请检查模板 Key 与版本是否重复');
        } catch (error: unknown) {
            message.error(resolveWorkflowErrorMessage(error, '保存失败，请检查配置'));
        }
    };

    const handleQueryTemplate = async () => {
        try {
            const values = await queryForm.validateFields();
            setIsEditing(false);
            await queryLatestTemplate({
                variables: { bizType: values.bizType },
            });
        } catch {
            message.warning('请输入业务类型后查询');
        }
    };

    const handleStartEdit = () => {
        if (!latestTemplate) {
            return;
        }
        const formValues = templateToFormValues(latestTemplate);
        editForm.setFieldsValue(formValues);
        setEditPreviewNodes(formValues.nodes);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        editForm.resetFields();
        setEditPreviewNodes([]);
    };

    const handleEditPreview = async () => {
        try {
            const values = await editForm.validateFields();
            setEditPreviewNodes(values.nodes || []);
        } catch {
            message.warning('请先完善模板必填信息再预览');
        }
    };

    const handleUpdateTemplate = async () => {
        if (!latestTemplate) {
            return;
        }
        try {
            const values = await editForm.validateFields();
            const result = await updateTemplate({
                variables: {
                    input: {
                        id: latestTemplate.id,
                        name: values.name,
                        status: values.status,
                        description: values.description,
                        definitionJson: buildDefinitionJson(values.nodes),
                    },
                },
            });
            if (result.data?.updateWorkflowTemplate) {
                message.success('流程模板更新成功');
                setIsEditing(false);
                await queryLatestTemplate({
                    variables: { bizType: latestTemplate.bizType },
                });
                return;
            }
            message.error('流程模板更新失败');
        } catch (error: unknown) {
            message.error(resolveWorkflowErrorMessage(error, '更新失败，请检查配置'));
        }
    };

    const latestTemplate = queryData?.workflowLatestTemplateByBizType ?? null;
    const displayNodes: WorkflowDefinitionNodeView[] = useMemo(() => {
        if (!latestTemplate?.definitionJson) {
            return [];
        }
        try {
            const parsed = JSON.parse(latestTemplate.definitionJson) as { nodes?: WorkflowDefinitionNodeView[] };
            return (parsed.nodes || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        } catch {
            return [];
        }
    }, [latestTemplate]);

    const displayApproverIds = useMemo(
        () => [...new Set(displayNodes.flatMap((node) => node.userIds || []))],
        [displayNodes],
    );

    const { data: headquartersAccountData } = useQuery<GetAccountListingForHeadquartersResponse>(
        GET_ACCOUNT_LISTING_FOR_HEADQUARTERS,
        {
            skip: displayApproverIds.length === 0,
            fetchPolicy: 'cache-first',
        },
    );

    const accountNameById = useMemo(() => {
        const map = new Map<number, string>();
        (headquartersAccountData?.allAccounts ?? []).forEach((account) => {
            const id = Number(account.id);
            if (!Number.isNaN(id) && account.name) {
                map.set(id, account.name);
            }
        });
        return map;
    }, [headquartersAccountData]);

    const displaySteps = useMemo(
        () =>
            displayNodes.map((node, index) => ({
                key: `${node.nodeKey}_${index}`,
                title: node.nodeName || `节点${index + 1}`,
                description: (
                    <Space direction="vertical" size={2}>
                        <Tag color={nodeTypeTagColorMap[node.nodeType] || 'default'}>
                            {nodeTypeLabelMap[node.nodeType] || `UNKNOWN(${node.nodeType})`}
                        </Tag>
                        <Text type="secondary">key: {node.nodeKey || '-'}</Text>
                        <Text type="secondary">
                            审批人: {formatApproverDisplay(node.userIds, accountNameById)}
                        </Text>
                    </Space>
                ),
            })),
        [displayNodes, accountNameById],
    );

    const displayNodeColumns = [
        { title: '顺序', dataIndex: 'order', key: 'order', width: 80 },
        { title: '节点Key', dataIndex: 'nodeKey', key: 'nodeKey' },
        { title: '节点名称', dataIndex: 'nodeName', key: 'nodeName' },
        {
            title: '节点类型',
            dataIndex: 'nodeType',
            key: 'nodeType',
            render: (nodeType: number) => (
                <Tag color={nodeTypeTagColorMap[nodeType] || 'default'}>
                    {nodeTypeLabelMap[nodeType] || `UNKNOWN(${nodeType})`}
                </Tag>
            ),
        },
        {
            title: '审批人',
            dataIndex: 'userIds',
            key: 'userIds',
            render: (userIds?: number[]) => formatApproverDisplay(userIds, accountNameById),
        },
    ];

    const configTab = (
        <Row gutter={16} className="mt-4">
            <Col span={14}>
                <Card title="模板配置">
                    <Form form={form} layout="vertical">
                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item
                                    label="模板Key"
                                    name="templateKey"
                                    rules={[{ required: true, message: '请输入模板Key' }]}
                                >
                                    <Input placeholder="template_key" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="模板名称"
                                    name="name"
                                    rules={[{ required: true, message: '请输入模板名称' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item
                                    label="业务类型"
                                    name="bizType"
                                    rules={[{ required: true, message: '请输入业务类型' }]}
                                >
                                    <Input placeholder="biz_type" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="模板状态"
                                    name="status"
                                    rules={[{ required: true, message: '请选择模板状态' }]}
                                >
                                    <Select
                                        options={[
                                            { label: '草稿(1)', value: 1 },
                                            { label: '启用(2)', value: 2 },
                                            { label: '停用(3)', value: 3 },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item
                                    label="版本"
                                    name="version"
                                    rules={[{ required: true, message: '请输入版本号' }]}
                                >
                                    <InputNumber min={1} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="模板描述" name="description">
                                    <Input placeholder="可选说明" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left">节点配置</Divider>
                        <Form.List name="nodes">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map((field, index) => (
                                        <Card
                                            size="small"
                                            key={field.key}
                                            title={`节点 ${index + 1}`}
                                            extra={
                                                <Button
                                                    danger
                                                    type="text"
                                                    icon={<MinusCircleOutlined />}
                                                    onClick={() => remove(field.name)}
                                                />
                                            }
                                            style={{ marginBottom: 12 }}
                                        >
                                            <Row gutter={12}>
                                                <Col span={8}>
                                                    <Form.Item
                                                        {...field}
                                                        label="节点Key"
                                                        name={[field.name, 'nodeKey']}
                                                        rules={[{ required: true, message: '必填' }]}
                                                    >
                                                        <Input placeholder="please input node_key" />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <Form.Item
                                                        {...field}
                                                        label="节点名称"
                                                        name={[field.name, 'nodeName']}
                                                        rules={[{ required: true, message: '必填' }]}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <Form.Item
                                                        {...field}
                                                        label="节点类型"
                                                        name={[field.name, 'nodeType']}
                                                        rules={[{ required: true, message: '必填' }]}
                                                    >
                                                        <Select
                                                            options={[
                                                                { label: 'APPROVAL', value: 'APPROVAL' },
                                                                { label: 'CC', value: 'CC' },
                                                                { label: 'SYSTEM', value: 'SYSTEM' },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Form.Item
                                                {...field}
                                                label="审批人ID列表（逗号分隔）"
                                                name={[field.name, 'approverUserIds']}
                                            >
                                                <Input placeholder="10001,10002" />
                                            </Form.Item>
                                        </Card>
                                    ))}
                                    <Form.Item>
                                        <Button
                                            type="dashed"
                                            onClick={() => add({ nodeType: 'APPROVAL', approverUserIds: '' })}
                                            icon={<PlusOutlined />}
                                            block
                                        >
                                            新增节点
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>

                        <Space>
                            <Button type="default" onClick={handlePreview}>
                                刷新预览
                            </Button>
                            <Button type="primary" onClick={handleBuildPayload}>
                                生成JSON（复制）
                            </Button>
                            <HasResource resourceCodes={[ResourceCode.menu.OPS_TOOL_SETTLEMENT]}>
                                <Button type="primary" loading={saving} onClick={handleSaveTemplate}>
                                    提交保存模板
                                </Button>
                            </HasResource>
                        </Space>
                    </Form>
                </Card>
            </Col>

            <Col span={10}>
                <Card title="流程可视化预览" style={{ marginBottom: 16 }}>
                    <Steps direction="vertical" current={Math.max(previewSteps.length - 1, 0)} items={previewSteps} />
                </Card>

                <Card title="节点摘要">
                    <Table rowKey={(row) => row.id || row.nodeKey} size="small" pagination={false} dataSource={previewNodes} columns={nodeColumns} />
                </Card>
            </Col>
        </Row>
    );

    const displayTab = (
        <div className="mt-4 space-y-4">
            <Card title="查询最新流程模板">
                <Form form={queryForm} layout="inline">
                    <Form.Item
                        label="业务类型"
                        name="bizType"
                        rules={[{ required: true, message: '请输入业务类型' }]}
                    >
                        <Input placeholder="biz_type" style={{ width: 280 }} />
                    </Form.Item>
                    <Form.Item>
                        <HasResource resourceCodes={[ResourceCode.menu.OPS_TOOL_SETTLEMENT]}>
                            <Button type="primary" loading={querying} onClick={handleQueryTemplate}>
                                查询
                            </Button>
                        </HasResource>
                    </Form.Item>
                </Form>
            </Card>

            <Card
                title="流程模板展示"
                extra={
                    latestTemplate && !isEditing ? (
                        <HasResource resourceCodes={[ResourceCode.menu.OPS_TOOL_SETTLEMENT]}>
                            <Button type="primary" onClick={handleStartEdit}>
                                修改审批模板
                            </Button>
                        </HasResource>
                    ) : null
                }
            >
                {!latestTemplate ? (
                    <Empty description="暂无模板数据，请输入业务类型后查询" />
                ) : isEditing ? (
                    <Row gutter={16}>
                        <Col span={14}>
                            <Form form={editForm} layout="vertical">
                                <Row gutter={12}>
                                    <Col span={12}>
                                        <Form.Item label="模板Key" name="templateKey">
                                            <Input disabled />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="模板名称"
                                            name="name"
                                            rules={[{ required: true, message: '请输入模板名称' }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={12}>
                                    <Col span={12}>
                                        <Form.Item label="业务类型" name="bizType">
                                            <Input disabled />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="模板状态"
                                            name="status"
                                            rules={[{ required: true, message: '请选择模板状态' }]}
                                        >
                                            <Select
                                                options={[
                                                    { label: '草稿(1)', value: 1 },
                                                    { label: '启用(2)', value: 2 },
                                                    { label: '停用(3)', value: 3 },
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={12}>
                                    <Col span={12}>
                                        <Form.Item label="版本" name="version">
                                            <InputNumber disabled style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="模板描述" name="description">
                                            <Input placeholder="可选说明" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Divider orientation="left">节点配置</Divider>
                                <Form.List name="nodes">
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map((field, index) => (
                                                <Card
                                                    size="small"
                                                    key={field.key}
                                                    title={`节点 ${index + 1}`}
                                                    extra={
                                                        <Button
                                                            danger
                                                            type="text"
                                                            icon={<MinusCircleOutlined />}
                                                            onClick={() => remove(field.name)}
                                                        />
                                                    }
                                                    style={{ marginBottom: 12 }}
                                                >
                                                    <Row gutter={12}>
                                                        <Col span={8}>
                                                            <Form.Item
                                                                {...field}
                                                                label="节点Key"
                                                                name={[field.name, 'nodeKey']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input placeholder="please input node_key" />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Form.Item
                                                                {...field}
                                                                label="节点名称"
                                                                name={[field.name, 'nodeName']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Form.Item
                                                                {...field}
                                                                label="节点类型"
                                                                name={[field.name, 'nodeType']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Select
                                                                    options={[
                                                                        { label: 'APPROVAL', value: 'APPROVAL' },
                                                                        { label: 'CC', value: 'CC' },
                                                                        { label: 'SYSTEM', value: 'SYSTEM' },
                                                                    ]}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                    <Form.Item
                                                        {...field}
                                                        label="审批人ID列表（逗号分隔）"
                                                        name={[field.name, 'approverUserIds']}
                                                    >
                                                        <Input placeholder="10001,10002" />
                                                    </Form.Item>
                                                </Card>
                                            ))}
                                            <Form.Item>
                                                <Button
                                                    type="dashed"
                                                    onClick={() => add({ nodeType: 'APPROVAL', approverUserIds: '' })}
                                                    icon={<PlusOutlined />}
                                                    block
                                                >
                                                    新增节点
                                                </Button>
                                            </Form.Item>
                                        </>
                                    )}
                                </Form.List>
                                <Space>
                                    <Button type="default" onClick={handleEditPreview}>
                                        刷新预览
                                    </Button>
                                    <Button onClick={handleCancelEdit}>取消</Button>
                                    <HasResource resourceCodes={[ResourceCode.menu.OPS_TOOL_SETTLEMENT]}>
                                        <Button type="primary" loading={updating} onClick={handleUpdateTemplate}>
                                            保存修改
                                        </Button>
                                    </HasResource>
                                </Space>
                            </Form>
                        </Col>
                        <Col span={10}>
                            <Card title="流程可视化预览" style={{ marginBottom: 16 }}>
                                <Steps
                                    direction="vertical"
                                    current={Math.max(editPreviewSteps.length - 1, 0)}
                                    items={editPreviewSteps}
                                />
                            </Card>
                            <Card title="节点摘要">
                                <Table
                                    rowKey={(row) => row.id || row.nodeKey}
                                    size="small"
                                    pagination={false}
                                    dataSource={editPreviewNodes}
                                    columns={nodeColumns}
                                />
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                        <div><Text strong>模板ID：</Text>{latestTemplate.id}</div>
                        <div><Text strong>模板Key：</Text>{latestTemplate.templateKey}</div>
                        <div><Text strong>模板名称：</Text>{latestTemplate.name}</div>
                        <div><Text strong>业务类型：</Text>{latestTemplate.bizType}</div>
                        <div><Text strong>版本：</Text>{latestTemplate.version}</div>
                        <div><Text strong>状态：</Text>{latestTemplate.status}</div>
                        <div><Text strong>描述：</Text>{latestTemplate.description || '-'}</div>
                        <Card size="small" title="流程可视化">
                            {displaySteps.length === 0 ? (
                                <Empty description="definitionJson 无可展示节点" />
                            ) : (
                                <Steps
                                    direction="vertical"
                                    current={Math.max(displaySteps.length - 1, 0)}
                                    items={displaySteps}
                                />
                            )}
                        </Card>
                        <Card size="small" title="节点摘要">
                            <Table
                                rowKey={(row) => `${row.nodeKey}_${row.order}`}
                                size="small"
                                pagination={false}
                                dataSource={displayNodes}
                                columns={displayNodeColumns}
                            />
                        </Card>
                        <Input.TextArea value={latestTemplate.definitionJson} autoSize={{ minRows: 8, maxRows: 20 }} readOnly />
                    </Space>
                )}
            </Card>
        </div>
    );

    return (
        <div className="p-6 space-y-4">
            <Title level={4}>审批流程模板可视化配置</Title>
            <Tabs
                className="mt-4"
                items={[
                    { key: 'config', label: '流程模板配置', children: configTab },
                    { key: 'display', label: '流程模板展示', children: displayTab },
                ]}
            />
        </div>
    );
};

export default WorkflowTemplateDesignerPage;
